"use client";
import {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";

interface AltchaProps {
  onStateChange?: (ev: CustomEvent) => void;
}

const Altcha = forwardRef<{ altchaValue: string | null }, AltchaProps>(
  ({ onStateChange }, ref) => {
    const widgetRef = useRef<HTMLElement>(null);
    const [altchaValue, setAltchaValue] = useState<string | null>(null);
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
      if (typeof window !== "undefined") {
        import("altcha").then(() => {
          if (typeof customElements !== "undefined") {
            const handleStateChange = (ev: Event) => {
              const customEvent = ev as CustomEvent;
              if (customEvent.detail?.payload) {
                setAltchaValue(customEvent.detail.payload);
                onStateChange?.(customEvent);
              }
            };

            const currentWidget = widgetRef.current;
            if (currentWidget) {
              currentWidget.addEventListener(
                "statechange",
                handleStateChange as EventListener,
              );
            }

            return () => {
              if (currentWidget) {
                currentWidget.removeEventListener(
                  "statechange",
                  handleStateChange as EventListener,
                );
              }
            };
          }
        });
      }
    }, [onStateChange]);

    useImperativeHandle(
      ref,
      () => ({
        altchaValue,
      }),
      [altchaValue],
    );

    useEffect(() => {
      setIsMounted(true);
    }, []);

    if (!isMounted) {
      return null;
    }

    return (
      <altcha-widget
        ref={widgetRef}
        style={{ "--altcha-max-width": "100%" }}
        challengeurl="/api/altcha"
      />
    );
  },
);

Altcha.displayName = "Altcha";
export { Altcha };
